import { Component, DOCUMENT, inject, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FuseConfig, FuseConfigService, Scheme } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FusePlatformService } from '@fuse/services/platform';
import { FUSE_VERSION } from '@fuse/version';
import { combineLatest, map, Subject, takeUntil } from 'rxjs';
import { EmptyLayoutComponent } from './layouts/empty/empty.component';
import { LoggedInMainComponent } from './layouts/logged-in-main/logged-in-main.component';
import { LoggedInSingleComponent } from './layouts/logged-in-single/logged-in-single.component';

@Component({
    selector: 'layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [EmptyLayoutComponent, LoggedInMainComponent, LoggedInSingleComponent],
})
export class LayoutComponent implements OnInit, OnDestroy {
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly document = inject(DOCUMENT);
    private readonly renderer2 = inject(Renderer2);
    private readonly fuseConfigService = inject(FuseConfigService);
    private readonly fuseMediaWatcherService = inject(FuseMediaWatcherService);
    private readonly fusePlatformService = inject(FusePlatformService);

    private readonly unsubscribeAll = new Subject<void>();

    config: FuseConfig;
    layout: string;
    scheme: Scheme;
    theme: string;

    ngOnInit(): void {
        this._initializeThemeAndScheme();
        this._subscribeToConfigChanges();
        this._setAppMetadata();
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    private _initializeThemeAndScheme(): void {
        combineLatest([
            this.fuseConfigService.config$,
            this.fuseMediaWatcherService.onMediaQueryChange$([
                '(prefers-color-scheme: dark)',
                '(prefers-color-scheme: light)',
            ]),
        ])
            .pipe(
                takeUntil(this.unsubscribeAll),
                map(([config, mql]) => {
                    const schemeMode = mql.breakpoints['(prefers-color-scheme: dark)'] ? 'dark' : 'light';
                    const scheme: Scheme = config.scheme === 'auto' ? schemeMode : config.scheme;
                    return { scheme, theme: config.theme };
                }),
            )
            .subscribe(({ scheme, theme }) => {
                this.scheme = scheme;
                this.theme = theme;
                this._updateScheme();
                this._updateTheme();
            });
    }

    private _subscribeToConfigChanges(): void {
        this.fuseConfigService.config$.pipe(takeUntil(this.unsubscribeAll)).subscribe((config) => {
            this.config = config;
            this._updateLayout();
        });
    }

    private _setAppMetadata(): void {
        const ngVersionElement = this.document.querySelector('[ng-version]');
        if (ngVersionElement) {
            this.renderer2.setAttribute(ngVersionElement, 'fuse-version', FUSE_VERSION);
        }
        this.renderer2.addClass(this.document.body, this.fusePlatformService.osName);
    }
    private _updateLayout(): void {
        // Get the current activated route
        let route = this.activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        // 1. Set the layout from the config
        this.layout = this.config.layout;

        // 2. Override with query parameter if present
        const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
        if (layoutFromQueryParam) {
            this.layout = layoutFromQueryParam;
            if (this.config) {
                this.config.layout = layoutFromQueryParam;
            }
        }

        // 3. Override with route data if present
        // Iterate through all paths to find layout configuration
        const paths = route.pathFromRoot;
        paths.forEach((path) => {
            if (path.routeConfig?.data?.layout) {
                this.layout = path.routeConfig.data.layout;
            }
        });
    }

    private _updateScheme(): void {
        this.document.body.classList.remove('light', 'dark');
        this.document.body.classList.add(this.scheme);
    }

    private _updateTheme(): void {
        // Remove previous theme classes
        this.document.body.classList.forEach((className: string) => {
            if (className.startsWith('theme-')) {
                this.document.body.classList.remove(className, className.split('-')[1]);
            }
        });

        // Add current theme class
        this.document.body.classList.add(this.theme);
    }
}
